import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { Button, Accordion, Alert, Row, Col } from "react-bootstrap";

import { useEcrUpdateMutation } from "api/memberPortal/ecr/ecr.api";
import {
  ECR_NOTE_RESPONSE_TYPES,
  EcrNoteResponseType,
  ECR_STATUSES,
} from "api/memberPortal/ecr/ecrOptions.types";
import {
  useEcrNotesQuery,
  useReleaseEcrResponseMutation,
} from "api/memberPortal/ecr/notes/ecrNotes.api";
import {
  EcrNote as EcrNoteType,
  EcrNoteStub,
} from "api/memberPortal/ecr/notes/ecrNotes.types";
import { useAuth } from "hooks/useAuth";

import EcrNote from "./EcrNote";
import {
  EcrNotesParams,
  OpenDeleteModal as OpenDeleteModalType,
  OpenNoteModal as OpenNoteModalType,
} from "./EcrNote.schema";
import EcrNoteForm from "./forms/EcrNoteForm";
import DeleteEcrNoteModal from "./modals/DeleteEcrNote.modal";

const ECR_NOTES: { total_notes: number; notes: EcrNoteType[] } = {
  total_notes: 0,
  notes: [],
};

const EcrNotes = ({ comment }: EcrNotesParams) => {
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  const [showNotes, setShowNotes] = useState(false);
  const [currentNote, setCurrentNote] = useState<
    EcrNoteType | null | undefined
  >(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inResponseTo, setInResponseTo] = useState<
    EcrNoteStub | EcrNoteType | null | undefined
  >(null);
  const [responseType, setResponseType] = useState<EcrNoteResponseType>(
    ECR_NOTE_RESPONSE_TYPES.ECR_CANCELLED,
  );
  const [expandAll, setExpandAll] = useState(false);
  const [toDeleteNote, setToDeleteNote] = useState<EcrNoteType | undefined>();
  const [alert, setAlert] = useState({ message: "", variant: "" });
  const { permissions } = useAuth();

  const { data: noteData = ECR_NOTES } = useEcrNotesQuery(
    comment.ecrid || skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const openNoteModal: OpenNoteModalType = (replyTo = null, note = null) => {
    if (note?.in_response_to) {
      setInResponseTo(note?.in_response_to);
    } else {
      setInResponseTo(replyTo);
    }
    setCurrentNote(note);
    setShowNotes(true);
  };

  const closeNoteModal = () => {
    setCurrentNote(null);
    setShowNotes(false);
  };

  const openDeleteModal: OpenDeleteModalType = (note) => {
    setToDeleteNote(note);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    setAlert({ message: "", variant: "" });
  }, [comment?.ecrid]);

  useEffect(() => {
    if (permissions?.role_type === "Lab") {
      setResponseType(ECR_NOTE_RESPONSE_TYPES.VALIDATOR);
    } else {
      setResponseType(ECR_NOTE_RESPONSE_TYPES.LAB);
    }
  }, [permissions]);

  const [triggerReleaseResponse] = useReleaseEcrResponseMutation();
  const [triggerUpdateEcr] = useEcrUpdateMutation();

  const releaseResponses = async () => {
    if (!comment) return;
    else {
      await triggerReleaseResponse({
        response_type: responseType,
        product: comment.pid?.product_id,
        maintenance: comment.maintenance_id?.maintenance_id,
      });
      const result = await triggerUpdateEcr({
        ecrid: comment.ecrid,
        status:
          responseType === ECR_NOTE_RESPONSE_TYPES.LAB
            ? ECR_STATUSES.RESPONSE_TO_LAB
            : ECR_STATUSES.RESPONSE_TO_VALIDATOR,
      });

      if (result.error) {
        setAlert({
          message: "Error releasing responses. Please try again.",
          variant: "success",
        });
      }

      if (result.data) {
        setAlert({
          message: `Responses Released! ECR Status set as Response to ${responseType}`,
          variant: "success",
        });
      }
    }
  };

  return (
    <Accordion flush>
      {alert?.message && (
        <Alert
          className="mt-3"
          variant={alert?.variant}
          onClose={() => setAlert({ message: "", variant: "" })}
          dismissible
        >
          {alert?.message}
        </Alert>
      )}
      <Accordion.Item eventKey="0" data-cy="ecr-notes-accordion">
        <Accordion.Header>
          <h6 className="fw-bold text-dark">NOTES</h6>
          <span className="border my-2 mx-4 p-2">
            Total: {noteData.total_notes}
          </span>
        </Accordion.Header>
        {(permissions?.role_type === "Lab" ||
          permissions?.is_senior ||
          permissions?.is_lead) &&
          permissions?.role_type !== "NIAP" && (
            <Row>
              <Col className="d-flex justify-content-end">
                <Button
                  data-cy="respond-to-note-button"
                  variant="warning rounded-pill m-1"
                  onClick={() => openNoteModal()}
                >
                  {permissions?.role_type === "Lab"
                    ? "Respond to Validator"
                    : "Response to Lab"}
                </Button>
                <Button
                  data-cy="release-responses-button"
                  variant="warning rounded-pill m-1"
                  onClick={releaseResponses}
                >
                  Release to {responseType}
                </Button>
              </Col>
            </Row>
          )}
        <Accordion.Body>
          {
            <div className="text-end">
              <button
                data-cy="add-note-button"
                className="btn btn-link d-inline-block"
                style={{ minWidth: 10 }}
                onClick={() => openNoteModal()}
              >
                Add
              </button>
            </div>
          }
          {noteData.notes.length > 0 &&
            (!expandAll ? (
              <div>
                <button
                  className="btn btn-primary d-inline-block"
                  style={{ minWidth: 10 }}
                  onClick={() => {
                    setExpandAll(true);
                    setSelectedNotes(
                      noteData.notes.map((note) => `${note.upid}`),
                    );
                  }}
                >
                  Expand All
                </button>
              </div>
            ) : (
              <div>
                <button
                  className="btn btn-primary d-inline-block"
                  style={{ minWidth: 10 }}
                  onClick={() => {
                    setExpandAll(false);
                    setSelectedNotes([]);
                  }}
                >
                  Collapse All
                </button>
              </div>
            ))}
          {noteData.notes.map((note) => (
            <div key={note.upid}>
              <EcrNote
                note={note}
                openNoteModal={openNoteModal}
                openDeleteModal={openDeleteModal}
                selectedNotes={selectedNotes}
                setSelectedNotes={setSelectedNotes}
              />
            </div>
          ))}
          {showNotes && comment && (
            <EcrNoteForm
              show={showNotes}
              noteType={inResponseTo ? "response" : "initiate"}
              handleClose={closeNoteModal}
              note={currentNote}
              comment={comment}
              replyTo={inResponseTo}
              responseType={responseType}
              setAlert={setAlert}
            />
          )}
          {showDeleteModal && (
            <DeleteEcrNoteModal
              toDeleteNote={toDeleteNote}
              onClose={() => setShowDeleteModal(false)}
              show={showDeleteModal}
            />
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default EcrNotes;
